/**
 * ELF-CIRCLE scheduler — section `#6b`, "This week's desk".
 *
 * Day tabs across the top, then the chosen day's times split into Morning,
 * Afternoon and Evening, with the confirmation card beside them. HubSpot
 * returns ~35 times a day in 15-minute steps; showing one part of one day at a
 * time keeps the grid a fixed, compact size — the old per-day columns with a
 * "+n more" fold grew the page by a whole day's list when opened.
 *
 * The week is live HubSpot availability, read through /api/scheduler. HubSpot
 * only ever returns slots that are still free, so a slot booked by anyone —
 * here, or straight from the HubSpot page — simply stops appearing. There is
 * deliberately no local cache and no slot state of our own: the grid is only
 * true at the moment it was fetched, and it re-reads after every booking.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import EditableImage from './EditableImage';
import EditableText from './EditableText';
import { apiHeaders, config } from '../../../auth/config';

interface Slot {
  label: string;
  startMillisUtc: number;
  endMillisUtc: number;
}

interface Day {
  dow: string;
  date: number;
  month: string;
  iso: string;
  /** Booked-capacity bar, 0–100. */
  load: number;
  slots: Slot[];
}

interface Availability {
  days: Day[];
  range: string;
  durationMinutes: number;
  timezone: string;
  isOffline: boolean;
}

/** Heading copy — admin-editable, unlike the dates below it. */
const KICKER = 'Schedule a consultation';
const TITLE = 'This week’s desk';

type Period = 'morning' | 'afternoon' | 'evening';

/** Parts of the day, by the slot label's hour (already in the meeting's zone). */
const PERIODS: { id: Period; label: string; from: number; to: number }[] = [
  { id: 'morning', label: 'Morning', from: 0, to: 12 },
  { id: 'afternoon', label: 'Afternoon', from: 12, to: 17 },
  { id: 'evening', label: 'Evening', from: 17, to: 24 },
];

function periodOf(slot: Slot): Period {
  const hour = Number(slot.label.slice(0, 2));
  return PERIODS.find((p) => hour >= p.from && hour < p.to)?.id ?? 'evening';
}

interface Selection {
  iso: string;
  startMillisUtc: number;
}

type BookState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'done'; when: string }
  | { kind: 'error'; message: string };

function formatSelection(day: Day, slot: Slot, durationMinutes: number, timezone: string): string {
  const end = new Date(slot.startMillisUtc + durationMinutes * 60_000);
  // The slot's own label is already in the meeting's timezone; render the end
  // in that same zone. It used to be pinned to India time, so a 30-minute New
  // York call read "13:00 — 23:00".
  const endLabel = new Date(slot.endMillisUtc || end).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  });
  return `${day.dow} ${day.date} ${day.month} · ${slot.label} — ${endLabel}`;
}

export const ElfScheduler: React.FC = () => {
  const [data, setData] = useState<Availability | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Day whose times are showing — independent of the selected slot. */
  const [viewIso, setViewIso] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('morning');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [book, setBook] = useState<BookState>({ kind: 'idle' });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/scheduler/availability`, { headers: apiHeaders() });
      const body = await res.json();
      if (!res.ok || !body.enabled || !body.availability) {
        setData(null);
        setLoadError(body.message || 'Times are unavailable right now');
        return;
      }
      setData(body.availability as Availability);
    } catch {
      setLoadError('Could not reach the calendar — please refresh');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const days = useMemo(() => data?.days ?? [], [data]);

  /**
   * Default to the first free slot so the confirm card is never empty, and
   * show that slot's day and part of the day.
   */
  useEffect(() => {
    if (selection || days.length === 0) return;
    const firstDay = days.find((d) => d.slots.length > 0);
    if (!firstDay) return;
    setSelection({ iso: firstDay.iso, startMillisUtc: firstDay.slots[0].startMillisUtc });
    setViewIso(firstDay.iso);
    setPeriod(periodOf(firstDay.slots[0]));
  }, [days, selection]);

  const viewDay = days.find((d) => d.iso === viewIso) ?? days.find((d) => d.slots.length > 0) ?? null;
  const counts = useMemo(() => {
    const c: Record<Period, number> = { morning: 0, afternoon: 0, evening: 0 };
    viewDay?.slots.forEach((slot) => (c[periodOf(slot)] += 1));
    return c;
  }, [viewDay]);
  const times = viewDay ? viewDay.slots.filter((slot) => periodOf(slot) === period) : [];

  /** Switching day lands on the first part of it that has times. */
  const openDay = (day: Day) => {
    setViewIso(day.iso);
    const c: Record<Period, number> = { morning: 0, afternoon: 0, evening: 0 };
    day.slots.forEach((slot) => (c[periodOf(slot)] += 1));
    if (!c[period]) setPeriod(PERIODS.find((p) => c[p.id] > 0)?.id ?? 'morning');
  };

  const selected = useMemo(() => {
    if (!selection) return null;
    const day = days.find((d) => d.iso === selection.iso);
    const slot = day?.slots.find((s) => s.startMillisUtc === selection.startMillisUtc);
    return day && slot ? { day, slot } : null;
  }, [days, selection]);

  const confirm = useCallback(async () => {
    if (!selected || !data) return;
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) return setBook({ kind: 'error', message: 'Please tell us your name' });
    if (!trimmedEmail) return setBook({ kind: 'error', message: 'Please give us an email address' });

    setBook({ kind: 'sending' });
    const [firstName, ...rest] = trimmedName.split(/\s+/);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/scheduler/book`, {
        method: 'POST',
        headers: apiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          startMillisUtc: selected.slot.startMillisUtc,
          durationMillis: data.durationMinutes * 60_000,
          firstName,
          lastName: rest.join(' '),
          email: trimmedEmail,
          phone: phone.trim(),
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        setBook({ kind: 'error', message: body.message || 'Could not confirm that time' });
        // A 4xx here usually means the slot went while the form was open, so
        // pull fresh availability rather than leaving a dead slot on screen.
        setSelection(null);
        void load();
        return;
      }
      setBook({
        kind: 'done',
        when: formatSelection(selected.day, selected.slot, data.durationMinutes, data.timezone),
      });
      setName('');
      setEmail('');
      setPhone('');
      setSelection(null);
      void load();
    } catch {
      setBook({ kind: 'error', message: 'Could not reach the server — please try again' });
    }
  }, [selected, data, name, email, phone, load]);

  return (
    <section id="scheduler" className="elf-band elf-band--flush">
      <div className="elf-sched__banner">
        <EditableImage slot="scheduler.banner" alt="" />
        <div className="elf-sched__banner-scrim" />
        <div className="elf-sched__banner-body">
          <div>
            <EditableText
              as="div"
              contentKey="scheduler.kicker"
              fallback={KICKER}
              label="Scheduler kicker"
              className="elf-sched__kicker"
            />
            <EditableText
              as="div"
              contentKey="scheduler.title"
              fallback={TITLE}
              label="Scheduler title"
              className="elf-sched__title"
            />
            <div className="elf-sched__range">
              {loading ? 'Loading available times…' : loadError ?? data?.range ?? ''}
            </div>
          </div>
        </div>
      </div>

      <div className="elf-sched__body">
        <div className="elf-band__inner">
          <div className="elf-sched__layout">
          <div className="elf-sched__picker">
            {days.length === 0 ? (
              <div className="elf-daycol__closed">
                {loading ? 'Loading available times…' : loadError ?? 'No times available at the moment'}
              </div>
            ) : (
              <>
                <div className="elf-sched__days" role="tablist" aria-label="Choose a day">
                  {days.map((day) => {
                    const on = day.iso === viewDay?.iso;
                    return (
                      <button
                        key={day.iso}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        className="elf-sched__day"
                        disabled={day.slots.length === 0}
                        onClick={() => openDay(day)}
                      >
                        <span className="elf-daycol__dow">{day.dow}</span>
                        <span className="elf-daycol__date">{day.date}</span>
                        <span className="elf-sched__daynote">
                          {day.slots.length ? `${day.slots.length} times` : 'Closed'}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="elf-sched__periods" role="tablist" aria-label="Choose a part of the day">
                  {PERIODS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      role="tab"
                      aria-selected={p.id === period}
                      className="elf-sched__period"
                      disabled={counts[p.id] === 0}
                      onClick={() => setPeriod(p.id)}
                    >
                      {p.label} <b>{counts[p.id]}</b>
                    </button>
                  ))}
                </div>

                <div className="elf-sched__times">
                  {times.map((slot) => {
                    const on =
                      selection?.iso === viewDay?.iso && selection?.startMillisUtc === slot.startMillisUtc;
                    return (
                      <button
                        key={slot.startMillisUtc}
                        type="button"
                        className={on ? 'slot on' : 'slot'}
                        aria-pressed={on}
                        onClick={() => {
                          if (!viewDay) return;
                          setSelection({ iso: viewDay.iso, startMillisUtc: slot.startMillisUtc });
                          setBook({ kind: 'idle' });
                        }}
                      >
                        {slot.label}
                      </button>
                    );
                  })}
                </div>
                {data && (
                  <div className="elf-sched__tz">
                    Times in {data.timezone.replace('_', ' ')} · {data.durationMinutes}-minute call
                  </div>
                )}
              </>
            )}
          </div>

          <div className="scard elf-sched__confirm">
            <div className="elf-sched__selected">
              <div className="mono">Selected</div>
              <div>
                {selected && data
                  ? formatSelection(selected.day, selected.slot, data.durationMinutes, data.timezone)
                  : loading
                    ? 'Loading…'
                    : 'Pick a time above'}
              </div>
            </div>

            {book.kind === 'done' ? (
              <div className="elf-sched__selected">
                <div className="mono">Confirmed</div>
                <div>{book.when}</div>
                <div>We&rsquo;ve emailed you the details.</div>
              </div>
            ) : (
              <>
                <input
                  className="fld"
                  placeholder="Full name"
                  aria-label="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <input
                  className="fld"
                  placeholder="Email address"
                  aria-label="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <input
                  className="fld"
                  placeholder="Mobile number"
                  aria-label="Mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                {book.kind === 'error' && (
                  <div className="elf-sched__selected" role="alert">
                    {book.message}
                  </div>
                )}
                <button
                  className="pillcta mag"
                  type="button"
                  disabled={!selected || book.kind === 'sending'}
                  onClick={() => void confirm()}
                >
                  {book.kind === 'sending' ? 'Confirming…' : 'Confirm'} <span>→</span>
                </button>
              </>
            )}
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ElfScheduler;
